// 3rd party libs
import { VictoryAxisProps } from 'victory';

// Types
import { DatePoint, Series } from 'types/graph';
import { DataAnalysisProperty, DataAnalysisPropTypes, KeyIndicatorHistory, StateCategory } from 'types/protein';

// Helpers
import { formatDate, getUserTimeZone, isUSTimeZone } from 'common/helpers/v1/dates';
import { getRandomColor } from 'common/helpers/v1/colors';

// Constants
export const CIRCLE_RADIUS = 10;
export const SCATTER_DISTANCE = 10;

// Theme
import theme from 'themes';

// Constants
import { proteinMachineCategoryStatesToString } from 'constants/proteinMachineCategoryStates';


const rescaleValues = (value: number, min: number | string, max: number | string): number => {
  const minNumber = typeof min === 'string' ? Number(min) || 0 : min;
  const maxNumber = typeof max === 'string' ? Number(max) || 1 : max;
  return Number(((maxNumber - minNumber) * value + minNumber).toFixed(1));
};

const normalizeValue = (value: number, min: number | string, max: number | string): number => {
  const minNumber = typeof min === 'string' ? Number(min) || 0 : min;
  const maxNumber = typeof max === 'string' ? Number(max) || 1 : max;
  return (value - minNumber) / (maxNumber - minNumber);
};

const colors = theme.colors.ProteinMachineStateCatgoryColors as Record<string, string>;
const labels = proteinMachineCategoryStatesToString as Record<string, string>;
// Pre-process series to specify a custom format for the tooltip content
//
//
export const formatLineSeriesTooltip = (
  series: Series[],
  min: number,
  max: number,
  timeZone?: string
): Series[] => {
  return series.map((s) => {
    return {
      ...s,
      tooltipFormat: (s, value) => {
        const displayDate: Date = value?.dateActual || value?.x;
        return isUSTimeZone(getUserTimeZone())
          ? `${s?.label}, ${value?.interpolationMessage ? `${value?.interpolationMessage} ` : ''}${
              typeof value?.y === 'number' ? rescaleValues(value?.y, min, max).toFixed(1) : value?.y
            } ${s?.unit}, ${formatDate(
              displayDate,
              'hours-minutes',
              timeZone,
              'en-US',
              true
            )} ${formatDate(displayDate, 'numeric-month-day', timeZone, 'en-US')}`
          : `${s?.label}, ${value?.interpolationMessage ? `${value?.interpolationMessage} ` : ''}${
              typeof value?.y === 'number' ? rescaleValues(value?.y, min, max).toFixed(1) : value?.y
            } ${s?.unit}, ${formatDate(displayDate, 'hours-minutes', timeZone)} ${formatDate(
              displayDate,
              'numeric-month-day',
              timeZone
            )}`;
      }
    };
  });
};

/**
 * Transform api response to <KPIOverTimeGraph /> input format
 * @param indicators
 * @param colors to pass different line colors
 */
export const toLineSeries = (indicators?: KeyIndicatorHistory[], colors?: string[]): Series[] => {
  const series: Series[] = [];
  indicators
    ?.filter((d) => d.values.length > 0)
    .forEach((indicator, index) => {
      const data: DatePoint[] = indicator.values.map((value) => {
        return {
          y: typeof value.value === 'number' ? value.value : 0,
          x: new Date(value.timestamp)
        };
      });
      const color =
        (colors && colors[index]) ??
        (theme.colors.kpiChartColors[series.length] || getRandomColor());
      series.push({
        mode: 'LINE',
        id: indicator.tagId,
        label: indicator.name || indicator.tagId,
        data,
        color : indicator?.color || color,
        optimize: true,
        unit: indicator?.unit || '',
        isLeftSide: indicator?.isLeftSide || false,
      });
    });
  return [...series];
};

/**
 * Transform api response to <KPIOverTimeGraph /> input format
 * @param statesCategories
 * @param min
 */
export const toScatterSeries = (
  statesCategories: StateCategory[],
  min: number,
  timeZone?: string
): Series => {
  const series: Series = {
    mode: 'SCATTER',
    bottom: SCATTER_DISTANCE,
    id: 'states',
    color: 'transparent',
    size: CIRCLE_RADIUS,
    fill: (d) => {
      const color = statesCategories.find(
        (a) => new Date(a.timestamp).toISOString() === d.toISOString()
      );
      if (!color) return 'black';
      return colors[color.name];
    },

    data: statesCategories.map((state, i) => {
      return {
        x: new Date(state.timestamp),
        y: min - SCATTER_DISTANCE,
        color: colors[statesCategories[i].name],
        label: isUSTimeZone(getUserTimeZone())
          ? `${labels[statesCategories[i].name]}, ${formatDate(
              state.timestamp,
              'hours-minutes',
              timeZone,
              'en-US',
              true
            )} ${formatDate(state.timestamp, 'numeric-month-day', timeZone, 'en-US')}`
          : `${labels[statesCategories[i].name]}, ${formatDate(
              state.timestamp,
              'hours-minutes',
              timeZone
            )} ${formatDate(state.timestamp, 'numeric-month-day', timeZone)}`
      };
    })
  };
  return series;
};

// Compute scatter points colors
export const color = (x: Date, states: StateCategory[]): string => {
  const state = states.find(
    (state) => x >= new Date(state.timestamp) && x <= new Date(state.endTimestamp)
  );
  return state ? colors[state.name] : '';
};

// Axis style
export const axisStyle = {
  vertical: {
    style: {
      grid: {
        stroke: 'none'
      },
      axis: {
        stroke: 'none'
      }
    }
  },
  horizontal: {
    style: {
      axis: { stroke: 'none' },
      grid: { stroke: theme.colors.lightGrey3 }
    }
  }
};


/**
 * Build Axis
 */
export const leftAxis = (
  max: number,
  min: number,
  dataParams: DataAnalysisProperty[]
): VictoryAxisProps | undefined => {
  if (!isFinite(max)) return undefined;
  const firstYAxisMin = dataParams.find(
    (dataParam) => dataParam.property === DataAnalysisPropTypes.FirstYAxisMin
  );
  const firstYAxisMax = dataParams.find(
    (dataParam) => dataParam.property === DataAnalysisPropTypes.FirstYAxisMax
  );
  const minDomain = typeof firstYAxisMin?.value === 'number' ? firstYAxisMin?.value : min;

  return {
    style: axisStyle.vertical.style,
    tickValues: [0, 0.25, 0.5, 0.75, 1],
    tickFormat: (t) => rescaleValues(t, min, max),
    ...(firstYAxisMin?.value !== null &&
      firstYAxisMax?.value !== null && {
        domain: [
          normalizeValue(minDomain, min, max),
          normalizeValue(firstYAxisMax?.value as number, min, max)
        ]
      })
  };
};

export const rightAxis = (
  max: number,
  min: number,
  dataParams: DataAnalysisProperty[]
): VictoryAxisProps | undefined => {
  if (!isFinite(max)) return undefined;
  const secondYAxisMin = dataParams?.find(
    (dataParam) => dataParam?.property === DataAnalysisPropTypes.SecondYAxisMin
  );
  const secondYAxisMax = dataParams?.find(
    (dataParam) => dataParam?.property === DataAnalysisPropTypes.SecondYAxisMax
  );
  const minDomain = typeof secondYAxisMin?.value === 'number' ? secondYAxisMin?.value : min;
  return {
    orientation: 'right',
    style: axisStyle.vertical.style,
    tickValues: [0, 0.25, 0.5, 0.75, 1],
    tickFormat: (t) => rescaleValues(t, min, max),
    ...(secondYAxisMin?.value !== null &&
      secondYAxisMax?.value !== null && {
        domain: [
          normalizeValue(minDomain, min, max),
          normalizeValue(secondYAxisMax?.value as number, min, max)
        ]
      })
  };
};

/**
 * Axis
 */
export const axisH = (timeZone?: string): VictoryAxisProps => {
  return {
    orientation: 'top',
    style: {
      axis: { stroke: 'none' },
      grid: { stroke: theme.colors.lightGrey3 }
    },
    tickFormat: (t) =>
      isUSTimeZone(getUserTimeZone())
        ? `${formatDate(t, 'hours-minutes', timeZone, 'en-US', true)}\n${formatDate(
            t,
            'numeric-month-day',
            timeZone,
            'en-US'
          )}`
        : `${formatDate(t, 'hours-minutes', timeZone)}\n${formatDate(
            t,
            'numeric-month-day',
            timeZone
          )}`
  };
};

export const axisV = (): VictoryAxisProps => {
  return {
    style: {
      grid: {
        stroke: 'none'
      },
      axis: {
        stroke: 'none'
      }
    }
  };
};

export const normalize = (series: Series[], max: number, min: number): Series[] =>
  series.map((s) => {
    return {
      ...s,
      data: s.data.map((d) => {
        if (max === min) {
          return { x: d.x, y: 1 };
        }
        return { x: d.x, y: typeof d.y === 'number' ? (d.y - min) / (max - min) : 1 };
      })
    };
  });
